import { subscribedUser } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Subscribed User Service
 * Handles all "Subscribed Users" admin API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/subscribed-user/list - Get subscribed user list
 * - GET /api/v1/admin/subscribed-user/get - Get a single subscribed user's subscription details
 * - GET /api/v1/admin/subscribed-user/transactions - Get renewal/transaction history for a subscription
 * - GET /api/v1/admin/subscribed-user/documents - Get the subscription owner's verification documents (WEAL-466)
 * - POST /api/v1/admin/subscribed-user/approve-verification - Approve the subscription owner's profile verification
 * - POST /api/v1/admin/subscribed-user/reject-verification - Reject the subscription owner's profile verification
 */
class SubscribedUserService {
  /**
   * @name getList
   * @description Fetches the subscribed user list from the admin API with pagination, search and filters.
   * @param {Object} params - Query parameters (search, plan_id, user_type, status, purchase_date_from,
   * purchase_date_to, page, limit, sort_by, order_by)
   * @returns {Promise<Object>} The response data containing the subscribed user list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(subscribedUser.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single subscribed user's subscription details by the subscription uuid.
   * @param {string} uuid - Subscription UUID (subscription_uuid from the list row, NOT a user uuid).
   * @returns {Promise<Object>} The response data containing subscription details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(subscribedUser.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getTransactions
   * @description Fetches the renewal/transaction history for a subscription.
   * @param {string} uuid - Subscription UUID (same uuid used by `get`).
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise<Object>} The response data containing the transaction list.
   */
  static async getTransactions(uuid, params = {}) {
    try {
      const response = await axios.get(subscribedUser.transactions, { params: { uuid, ...params } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getDocuments
   * @description Fetches the subscription owner's uploaded verification documents plus the
   * current verification status/rejection reason (WEAL-466).
   * @param {string} uuid - Subscription UUID (same uuid used by `get`).
   * @returns {Promise<Object>} The response data containing { context, verification_status, rejection_reason, documents }.
   */
  static async getDocuments(uuid) {
    try {
      const response = await axios.get(subscribedUser.documents, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name approveVerification
   * @description Approves the subscription owner's profile verification.
   * @param {string} uuid - Subscription UUID.
   * @returns {Promise<Object>} The response data.
   */
  static async approveVerification(uuid) {
    try {
      const response = await axios.post(subscribedUser.approveVerification, { uuid });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name rejectVerification
   * @description Rejects the subscription owner's profile verification with a reason.
   * @param {string} uuid - Subscription UUID.
   * @param {string} reason - Rejection reason shown to the user.
   * @returns {Promise<Object>} The response data.
   */
  static async rejectVerification(uuid, reason) {
    try {
      const response = await axios.post(subscribedUser.rejectVerification, { uuid, reason });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { SubscribedUserService };
